import React, { useEffect, useState, useRef } from "react";
import {
  Card, CardBody, Row, Col, Input, Button, Table, Badge,
  Modal, ModalHeader, ModalBody,
  ModalFooter
} from "reactstrap";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie, reloadWindow, textCapitalize } from "../../Helpers/Utils";

const POS = () => {

  const paymentInputRef = useRef(null);
  const customerSelectRef = useRef(null);

  const [discountValue, setDiscountValue] = useState(0); // Input-la user type panra number
  const [discountType, setDiscountType] = useState("FLAT"); // FLAT or PERCENTAGE

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);
  const loading = useSelector(state => state.loading.loading);

  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "" });
  /* ================= PRODUCTS ================= */
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  /* ================= CART ================= */
  const [cart, setCart] = useState([]);

  /* ================= CUSTOMER ================= */
  const [customerOption, setCustomerOption] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(0);

  /* ================= SALE / PAYMENT ================= */
  const [saleData, setSaleData] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [showBillBtns, setShowBillBtns] = useState(false);

  const [loyaltyBalance, setLoyaltyBalance] = useState(0);


  /* ================= PREVIEW ================= */
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [thermalOpen, setThermalOpen] = useState(false);

  /* ================= BARCODE ================= */
  const barcodeBuffer = useRef("");
  const barcodeTimer = useRef(null);

  /* =================================================
     LOAD PRODUCTS
  ================================================= */


  const loadProducts = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "get",
      url: `/products/search.php?outlet_id=${user.outlet_id}&q=${search}`
    });
    if (res?.success) setProducts(res.data || []);
    dispatch(loadingFalse());
  };

  const cartSubtotal = cart.reduce(
    (sum, i) => sum + (i.price * i.qty),
    0
  );

  const cgstTotal = cart.reduce((sum, i) => {
    const half = Number(i.gst_rate || 0) / 2;
    return sum + ((i.price * i.qty) * half) / 100;
  }, 0);

  const sgstTotal = cart.reduce((sum, i) => {
    const half = Number(i.gst_rate || 0) / 2;
    return sum + ((i.price * i.qty) * half) / 100;
  }, 0);

  const calculatedManualDiscount = discountType === "PERCENTAGE"
    ? (cartSubtotal * Number(discountValue || 0)) / 100
    : Number(discountValue || 0);

  const grandTotal = cartSubtotal + cgstTotal + sgstTotal - calculatedManualDiscount;

  const payableAmount = Math.max(0, cartSubtotal - calculatedManualDiscount);


  useEffect(() => { loadProducts(); }, [search]);

  const handleBarcode = (code) => {
    for (const p of products) {
      if (p.meta?.barcode === code) return addToCart(p);
      for (const v of p.variants || []) {
        if (v.barcode === code) return addToCart(p, v);
      }
    }
    toast.error("Barcode not found");
  };



  /* =================================================
     CART
  ================================================= */
  const addToCart = (p, v = null) => {
    const key = v ? `v-${v.variant_id}` : `p-${p.product_id}`;
    setCart(c => {
      const e = c.find(i => i.key === key);
      if (e) return c.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...c, {
        key,
        product_id: p.product_id,
        variant_id: v?.variant_id || null,
        name: v ? `${p.name} (${v.name})` : p.name,
        qty: 1,
        price: v ? v.discounted_price : p.discounted_price,
        gst_rate: v ? v.gst_rate : p.gst_rate
      }];
    });
  };

  const updateQty = (key, qty) => {
    if (qty <= 0) setCart(c => c.filter(i => i.key !== key));
    else setCart(c => c.map(i => i.key === key ? { ...i, qty } : i));
  };

  /* =================================================
     CUSTOMER SEARCH + LOYALTY
  ================================================= */
  const searchCustomers = async (input) => {
    if (!input) return [];
    const res = await Handler({
      method: "get",
      url: `/customers/search_cus.php?q=${input}&outlet_id=${user.outlet_id}`
    });
    if (!res?.success) return [];
    return res.data.map(c => ({
      value: c.id,
      label: `${c.name} - ${c.phone}`
    }));
  };

  const selectCustomer = async (opt) => {
    if (!opt) {
      setCustomerOption(null);
      setCustomerInfo(null);
      setLoyaltyBalance(0);
      setRedeemPoints(0);
      return;
    }

    setCustomerOption(opt);

    const res = await Handler({
      method: "post",
      url: `/customers/view.php?id=${opt.value}`
    });
    if (res?.success) setCustomerInfo(res.data);

    // 🔥 FETCH LOYALTY BALANCE
    const lp = await Handler({
      method: "post",
      url: `/loyalty_points/balance.php`,
      data: { customer_id: opt?.value, outlet_id: user?.outlet_id }
    });
    if (lp?.success) setLoyaltyBalance(lp.data.balance || 0);
  };

  /* =================================================
     CREATE SALE
  ================================================= */
  const createSale = async () => {
    if (!customerOption) return toast.error("Select customer");
    if (!cart.length) return toast.error("Cart empty");

    dispatch(loadingTrue());

    const res = await Handler({
      method: "post",
      url: "/sales/create.php",
      data: {
        outlet_id: user.outlet_id,
        customer_id: customerOption.value,
        items: cart.map(i => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          quantity: i.qty
        }))
      }
    });

    dispatch(loadingFalse());

    if (!res?.success) return toast.error(res?.msg);

    setSaleData(res.data);
    setPaymentOpen(true);
    toast.success("Sale created. Proceed to payment");
  };

  /* =================================================
     PAYMENT
  ================================================= */
  const createPayment = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "post",
      url: "/payments/create.php",
      data: {
        sale_id: saleData.sale_id,
        payment_mode: paymentMode,
        meta: {
          discount_type: discountType,        // 🔥 Added
          discount_value: Number(discountValue), // 🔥 Added
          manual_discount: Number(calculatedManualDiscount), // Final money value
          redeem_points: redeemPoints,
          note: "Paid at counter"
        }
      }
    });

    dispatch(loadingFalse());
    if (!res?.success) {
      toast.error(res?.msg);
      return;
    }
    setPaymentOpen(false);
    setCart([]);
    toast.success("Payment completed");
    openPdf(
      // "http://localhost/bill_backend_production/apis/invoices/receipt.php"
      // "http://localhost/billing-with-quotation/apis/invoices/receipt.php"
      "https://whatsapp.billpro360.com/bill/apis/invoices/receipt.php"
    );

    setTimeout(() => {
      reloadWindow();
    }, 1500);
  };


  /* =================================================
     PDF OPEN
  ================================================= */

  const openPdf = (url) => {
    // const token = localStorage.getItem("token"); // or cookie util
    const token = getCookie('token')
    if (!token) {
      toast.error("Login expired");
      return;
    }
    const w = window.open("", "_blank");
    w.document.write("Generating...");

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`   // 🔥 THIS FIX
      },
      body: JSON.stringify({
        sale_id: saleData.sale_id,
        outlet_id: user.outlet_id
      })
    })
      .then(res => {
        if (!res.ok) throw new Error("Unauthorized");
        return res.blob();
      })
      .then(blob => {
        const fileURL = URL.createObjectURL(blob);
        w.location.href = fileURL;
      })
      .catch(() => {
        w.close();
        toast.error("Unauthorized / Session expired");
      });
  };

  const subtotal = cart.reduce(
    (sum, i) => sum + i.price * i.qty,
    0
  );

  const totalGST = cart.reduce(
    (sum, i) => sum + ((i.price * i.qty) * i.gst_rate) / 100,
    0
  );

  useEffect(() => {
    const handleSpace = (e) => {
      if (e.code !== "Space") return;

      if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) return;
      if (paymentOpen) return;
      if (!cart.length) return;

      if (!customerOption) {
        toast.error("Select customer");
        return;
      }

      e.preventDefault();
      createSale();
    };

    window.addEventListener("keydown", handleSpace);
    return () => window.removeEventListener("keydown", handleSpace);
  }, [cart, paymentOpen, customerOption]);

  useEffect(() => {
    const handler = (e) => {

      if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) return;

      // barcode ENTER
      if (e.key === "Enter" && barcodeBuffer.current.length >= 4) {
        handleBarcode(barcodeBuffer.current);
        barcodeBuffer.current = "";
        return;
      }

      if (/^[a-zA-Z0-9]$/.test(e.key)) {
        barcodeBuffer.current += e.key;

        clearTimeout(barcodeTimer.current);
        barcodeTimer.current = setTimeout(() => {
          barcodeBuffer.current = "";
        }, 120);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [products]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "F9") return;

      if (!cart.length) return toast.error("Cart empty");
      if (!customerOption) return toast.error("Select customer");
      if (paymentOpen) return;

      e.preventDefault();
      createSale();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cart, customerOption, paymentOpen]);



  useEffect(() => {
    if (!paymentOpen) return;

    const handler = (e) => {

      // 🔴 F10 → ALWAYS PAY (even input focused)
      if (e.key === "F10") {
        e.preventDefault();
        e.stopPropagation();
        createPayment();
        return;
      }

      // 🔢 Payment mode shortcuts
      if (e.key === "1") setPaymentMode("CASH");
      if (e.key === "2") setPaymentMode("UPI");
      if (e.key === "3") setPaymentMode("CARD");

      // ❌ ESC cancel
      if (e.key === "Escape") {
        e.preventDefault();
        setPaymentOpen(false);
        reloadWindow();
      }
    };

    // 🔥 CAPTURE PHASE → browser F10 block aagathu
    window.addEventListener("keydown", handler, true);

    return () =>
      window.removeEventListener("keydown", handler, true);

  }, [paymentOpen, createPayment]);


  useEffect(() => {
    const handler = (e) => {

      // ignore typing
      if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) return;

      // ENTER → barcode
      if (e.key === "Enter" && barcodeBuffer.current.length >= 4) {
        handleBarcode(barcodeBuffer.current);
        barcodeBuffer.current = "";
        return;
      }

      // barcode chars only
      if (/^[a-zA-Z0-9]$/.test(e.key)) {
        barcodeBuffer.current += e.key;

        clearTimeout(barcodeTimer.current);
        barcodeTimer.current = setTimeout(() => {
          barcodeBuffer.current = "";
        }, 120);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [products]);


  useEffect(() => {
    const handler = (e) => {

      // 🔵 F2 → focus customer search
      if (e.key === "F2") {
        e.preventDefault();
        customerSelectRef.current?.focus();
      }

      // 🔴 ESC → clear selected customer
      if (e.key === "Escape" && customerOption) {
        setCustomerOption(null);
        setCustomerInfo(null);
        setLoyaltyBalance(0);
        setRedeemPoints(0);
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [customerOption]);


  return (
    <>
      <Row className="mb-3">
        <Col><h5 className="fw-bold">POS</h5></Col>
      </Row>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody className="p-2">
          <div className="row g-2 small align-items-center">
            <div className="col-2 fw-bold"> Quick Keyboard Help :</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F2</Badge> Customer</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F9</Badge> Create Sale</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F10</Badge> Pay</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">ESC</Badge> Cancel</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">1</Badge> Cash</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">2</Badge> UPI</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">3</Badge> Card</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">Enter</Badge> Barcode</div>
          </div>
        </CardBody>
      </Card>


      <Row className="g-3">
        {/* LEFT */}
        <Col md={8}>
          <Input
            placeholder="Scan / Search product"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-3"
          />

          <Card className="shadow-sm">
            <CardBody className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>SKU / Barcode</th>
                    <th className="text-end">Stock</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Add</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <React.Fragment key={p.product_id}>
                      <tr>
                        <td className="fw-bold">{p.name}</td>
                        <td className="small text-muted">
                          SKU: {p.meta?.sku || "-"}<br />
                          BC: {p.meta?.barcode || "-"}
                        </td>
                        <td className="text-end">
                          <Badge color={p.quantity > 0 ? "success" : "danger"}>
                            {p.quantity}
                          </Badge>
                        </td>
                        <td className="text-end fw-bold">₹ {Number(p.discounted_price).toFixed(2)}</td>
                        <td className="text-end">
                          <Button size="sm" disabled={p.quantity <= 0 || loading} color="primary" onClick={() => addToCart(p)}>+</Button>
                        </td>
                      </tr>

                      {p?.variants?.map(v => (
                        <tr key={v.variant_id} className="text-muted">
                          <td className="ps-4">↳ {v.name}</td>
                          <td className="small">
                            SKU: {v.sku || "-"}<br />
                            BC: {v.barcode || "-"}
                          </td>
                          <td className="text-end">{v.quantity}</td>
                          <td className="text-end">₹ {Number(v.discounted_price).toFixed(2)}</td>
                          <td className="text-end">
                            <Button size="sm" color="warning" disabled={v.quantity <= 0 || loading} onClick={() => addToCart(p, v)}>+</Button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT */}
        <Col md={4}>
          <Card className="shadow-sm">
            <CardBody>

              <Card className="mb-2">
                <CardBody className="p-2">
                  <Row className="g-2">
                    <Col xs={12}>
                      <Input
                        placeholder="Customer Name"
                        value={newCustomer.name}
                        onChange={(e) =>
                          setNewCustomer({ ...newCustomer, name: e.target.value })
                        }
                      />
                    </Col>
                    <Col xs={12}>
                      <Input
                        placeholder="Mobile Number"
                        maxLength={10}
                        value={newCustomer.phone}
                        onChange={(e) =>
                          setNewCustomer({ ...newCustomer, phone: e.target.value })
                        }
                      />
                    </Col>
                    <Col xs={12}>
                      <Button
                        disabled={loading}
                        color="success"
                        onClick={async () => {
                          if (!newCustomer.name || !newCustomer.phone)
                            return toast.error("Name & Mobile required");

                          const res = await Handler({
                            method: "post",
                            url: "/customers/add.php",
                            data: {
                              name: newCustomer.name,
                              phone: newCustomer.phone,
                              outlet_id: user.outlet_id
                            }
                          });

                          if (!res?.success) return toast.error(res?.msg);

                          // setCustomerOption({
                          //   value: res.data.id,
                          //   label: `${res.data.name} - ${res.data.phone}`
                          // });

                          setCustomerInfo(res.data);
                          setNewCustomer({ name: "", phone: "" });

                          toast.success("Customer added");
                        }}
                      >
                        Add New Customer
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>


              <AsyncSelect
                ref={customerSelectRef}
                cacheOptions
                loadOptions={searchCustomers}
                value={customerOption}
                onChange={selectCustomer}
                placeholder="Search customer"
                isClearable
              />

              {customerInfo && (
                <div className="mt-2 small text-muted">
                  <b>{customerInfo.name}</b><br />
                  {customerInfo.phone}<br />
                  <span className="text-success">
                    Loyalty Points: {loyaltyPoints}
                  </span>
                </div>
              )}

              <hr />

              {cart && cart?.map(i => {
                const itemTotal = i.price * i.qty;
                const halfGST = Number(i.gst_rate || 0) / 2;
                const cgst = (itemTotal * halfGST) / 100;
                const sgst = (itemTotal * halfGST) / 100;

                return (
                  <div key={i.key} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <b>{textCapitalize(i.name)}</b>
                        <div className="small text-muted">
                          ₹{i.price} × {i.qty}
                          <span className="ms-2">(GST {i.gst_rate}%)</span>
                        </div>
                        {i.gst_rate > 0 && (
                          <div className="small text-muted mt-1">
                            <div>CGST ({halfGST}%) : ₹ {cgst.toFixed(2)}</div>
                            <div>SGST ({halfGST}%) : ₹ {sgst.toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                      <div className="text-end">
                        <div className="fw-bold">
                          ₹ {itemTotal.toFixed(2)}
                        </div>
                        <Button disabled={loading} size="sm" color="danger" outline className="mt-1" onClick={() => updateQty(i.key, 0)} >
                          <i className='bx  bx-trash'></i>
                        </Button>
                      </div>
                    </div>

                  </div>
                );
              })}
              <hr />

              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>CGST</span>
                <span>₹ {cgstTotal.toFixed(2)}</span>
              </div>

              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>SGST</span>
                <span>₹ {sgstTotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold fs-5">
                  ₹ {Number(grandTotal).toFixed(2)}
                </span>
              </div>
              {!showBillBtns && (
                <Button disabled={loading} color="success" className="w-100" onClick={createSale}>
                  Create Sale
                </Button>
              )}

              {showBillBtns && (
                <>
                  <Button
                    disabled={loading}
                    color="primary"
                    className="w-100 mb-2"
                    onClick={() =>
                      openPdf(
                        "https://whatsapp.billpro360.com/bill/apis/invoices/receipt.php"
                        // "http://localhost/bill_backend_production/apis/invoices/receipt.php"
                        // "http://localhost/billing-with-quotation/apis/invoices/receipt.php"
                      )
                    }
                  >
                    Thermal Receipt
                  </Button>

                  <Button
                    disabled={loading}
                    color="secondary"
                    className="w-100"
                    onClick={() =>
                      openPdf(
                        // "http://localhost/bill_backend_production/apis/invoices/generate.php"
                        // "http://localhost/billing-with-quotation/apis/invoices/generate.php"
                        "https://whatsapp.billpro360.com/bill/apis/invoices/generate.php"
                      )
                    }
                  >
                    Invoice PDF
                  </Button>

                </>
              )}

            </CardBody>
          </Card>
        </Col>

        {/* PAYMENT MODAL */}
        <Modal isOpen={paymentOpen} backdrop="static" onOpened={() => {
          setTimeout(() => {
            if (loyaltyBalance > 0) {
              document.getElementById("redeemInput")?.focus();
            } else {
              paymentInputRef.current?.focus();
            }
          }, 150);
        }}>
          <ModalHeader>Payment</ModalHeader>
          <ModalBody>

            <div className="mb-3">
              {cart?.map(i => (
                <div key={i.key} className="d-flex justify-content-between small mb-1">
                  <div>
                    {textCapitalize(i.name)} × {i.qty}
                  </div>
                  <div>₹ {(i.price * i.qty).toFixed(2)}</div>
                </div>
              ))}

              <hr className="my-2" />

              <div className="d-flex justify-content-between fw-bold mb-2">
                <span>Subtotal</span>
                <span>₹ {Number(cartSubtotal).toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>CGST</span>
                <span>₹ {cgstTotal.toFixed(2)}</span>
              </div>

              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>SGST</span>
                <span>₹ {sgstTotal.toFixed(2)}</span>
              </div>

              {/* 🔥 Updated Total Section */}
              <div className="d-flex justify-content-between border-top pt-2">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold fs-5 text-primary">
                  ₹ {Number(grandTotal).toFixed(2)}
                </span>
              </div>
            </div>

            {loyaltyBalance > 0 && (
              <div className="mt-2">
                <div className="small text-muted fw-bold">
                  Available Loyalty Points: <b>{loyaltyBalance}</b>
                </div>

                <Input
                  id="redeemInput"
                  type="number"
                  min={0}
                  max={loyaltyBalance}
                  placeholder="Redeem points"
                  value={redeemPoints}
                  onChange={(e) =>
                    setRedeemPoints(
                      Math.min(Number(e.target.value), loyaltyBalance)
                    )
                  }
                />
                <div className="small mt-1 mb-3 text-success">
                  Redeem Value: ₹{Number(redeemPoints).toFixed(2)}
                </div>
              </div>
            )}

            {/* 🔥 Updated Manual Discount with Toggle */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="small fw-bold">Manual Discount</label>
                <div className="btn-group btn-group-sm">
                  <Button
                    type="button"
                    color={discountType === "FLAT" ? "dark" : "outline-dark"}
                    onClick={() => { setDiscountType("FLAT"); setDiscountValue(0); }}
                    style={{ padding: '2px 10px' }}
                  >₹</Button>
                  <Button
                    type="button"
                    color={discountType === "PERCENTAGE" ? "dark" : "outline-dark"}
                    onClick={() => { setDiscountType("PERCENTAGE"); setDiscountValue(0); }}
                    style={{ padding: '2px 10px' }}
                  >%</Button>
                </div>
              </div>
              <Input
                innerRef={paymentInputRef}
                type="number"
                min={0}
                max={discountType === "PERCENTAGE" ? 100 : cartSubtotal}
                placeholder={discountType === "PERCENTAGE" ? "Enter %" : "Enter Amount"}
                value={discountValue}
                onChange={e => {
                  let val = Number(e.target.value || 0);
                  let maxLimit = discountType === "PERCENTAGE" ? 100 : cartSubtotal;
                  setDiscountValue(Math.min(val, maxLimit));
                }}
              />
              {discountType === "PERCENTAGE" && discountValue > 0 && (
                <div className="small text-muted mt-1 text-end">
                  Discount Amount: <b>- ₹{calculatedManualDiscount.toFixed(2)}</b>
                </div>
              )}
            </div>

            <label className="small fw-bold">Payment Mode</label>
            <Input
              type="select"
              className="mb-3"
              value={paymentMode}
              onChange={e => setPaymentMode(e.target.value)}
            >
              <option value="CASH">Cash (1)</option>
              <option value="UPI">UPI (2)</option>
              <option value="CARD">Card (3)</option>
            </Input>

            {loyaltyPoints > 0 && (
              <Input
                type="number"
                placeholder={`Redeem points (max ${loyaltyPoints})`}
                value={redeemPoints}
                min={0}
                max={loyaltyPoints}
                onChange={e => setRedeemPoints(e.target.value)}
                className="mb-3"
              />
            )}
          </ModalBody>
          <ModalFooter className="d-flex gap-4">
            <Button disabled={loading} color="success" onClick={createPayment}>Confirm Payment</Button>
            <Button disabled={loading} color="danger" onClick={() => { setPaymentOpen(false); reloadWindow() }}>Cancel & Create New Sale</Button>
          </ModalFooter>
        </Modal>

      </Row>
    </>
  );
};

export default POS;
